// CRM Organisations List
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Plus, Search, Building2 } from 'lucide-react'
import { getCrmOrgs, createCrmOrg, deleteCrmOrg } from '../../../services/api'
import type { CrmOrganisation } from '../../../types'
import OrgForm from '../../../components/crm/OrgForm'
import PageHeader from '../../../components/ui/PageHeader'
import EmptyState from '../../../components/ui/EmptyState'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'

export default function OrganisationsList() {
  const { projectId } = useParams<{ projectId: string }>()
  const [orgs, setOrgs] = useState<CrmOrganisation[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState('')
  const [pendingDelete, setPendingDelete] = useState<CrmOrganisation | null>(null)

  const loadOrgs = () => {
    if (!projectId) return
    setLoading(true)
    getCrmOrgs(projectId)
      .then((data) => setOrgs(data.organisations || []))
      .catch(() => setOrgs([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadOrgs() }, [projectId])

  const filtered = orgs.filter((o) => {
    const q = search.toLowerCase()
    return !q || `${o.org_name} ${o.domain} ${o.industry}`.toLowerCase().includes(q)
  })

  const handleCreate = async (data: Partial<CrmOrganisation>) => {
    if (!projectId) return
    setError('')
    try {
      await createCrmOrg({ ...data, project_id: projectId })
      setShowForm(false)
      loadOrgs()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organisation')
    }
  }

  // Honest delete (P7): real mutation inside the ConfirmDialog. On failure it
  // throws, the dialog stays open and shows the message — never a fake success.
  const confirmDelete = async () => {
    if (!projectId || !pendingDelete) return
    await deleteCrmOrg(pendingDelete.org_id, projectId)
    loadOrgs()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Organisations"
        description="Companies, institutions, and teams in this project's network — search, add, and open an organisation to see its contacts."
        helpKey="crm"
        status={{ label: `(${filtered.length})` }}
        primaryAction={
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600">
            <Plus size={16} /> New Organisation
          </button>
        }
      />

      {error && (
        <div className="flex items-center justify-between gap-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-xs text-red-400 hover:text-red-600 shrink-0">Dismiss</button>
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input type="text" placeholder="Search organisations..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-sky-500/10 rounded-lg animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={search ? 'No organisations match your search' : 'No organisations yet'}
          description={search
            ? 'Try a different name, domain, or industry.'
            : 'Add your first organisation to group the companies and institutions in this project.'}
          action={search ? undefined : (
            <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600">
              <Plus size={16} /> Add an organisation
            </button>
          )}
        />
      ) : (
        <div className="light-card divide-y divide-sky-200/30">
          {filtered.map((o) => (
            <div key={o.org_id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/30">
              <Link to={`/project/${projectId}/crm/organisations/${o.org_id}`} className="flex-1 flex items-center gap-3">
                <div className="w-9 h-9 bg-purple-500/10 rounded-lg flex items-center justify-center text-xs font-semibold text-purple-700">
                  {o.org_name?.[0]}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{o.org_name}</p>
                  <p className="text-xs text-gray-500">{o.industry || o.domain || 'No details'}</p>
                </div>
              </Link>
              <button onClick={() => setPendingDelete(o)} className="text-xs text-red-400 hover:text-red-600">Delete</button>
            </div>
          ))}
        </div>
      )}

      {showForm && <OrgForm onSubmit={handleCreate} onClose={() => setShowForm(false)} />}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete organisation?"
        message={
          pendingDelete
            ? `This permanently removes ${pendingDelete.org_name} and its interactions from this project. This can't be undone.`
            : ''
        }
        confirmLabel="Delete organisation"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  )
}
